import React, { useCallback } from 'react';
import type { ColDef, ValueFormatterParams } from 'ag-grid-community';
import { LopCrudPage } from '../../components/LopCrudPage';
import { getOptionsBlackScholes } from '../../api/optionsBlackScholes';
import type { OptionBlackScholes } from '../../types/OptionBlackScholes';
import { t } from '../../i18n/t';
import { formatDateOnly } from '../../utils/dateTime';

// "אופציות בלק-שולץ" — read-only viewer over the tenant OptionsBlackScholes_view,
// for debug / user explanation. Reuses the LopCrudPage shell to inherit search /
// export / customization / context menu; Add/Delete off, saveBatch is a no-op.
// The intermediate columns (d1/d2/N(...)) are shown on purpose — they ARE the
// explanation of how each BS value was derived.

function fmtDate(p: ValueFormatterParams<OptionBlackScholes>): string {
  return formatDateOnly(p.value);
}

const N = { editable: false, cellDataType: 'number' as const };

const columnDefs: ColDef<OptionBlackScholes>[] = [
  // identity
  { field: 'assetId', headerName: t('optionsBlackScholes.columns.assetId'), width: 120, ...N },
  { field: 'assetTypeId', headerName: t('optionsBlackScholes.columns.assetTypeId'), width: 100, ...N },
  { field: 'callOrPut', headerName: t('optionsBlackScholes.columns.callOrPut'), width: 90, editable: false },
  { field: 'strike', headerName: t('optionsBlackScholes.columns.strike'), width: 110, ...N },
  { field: 'expDate', headerName: t('optionsBlackScholes.columns.expDate'), width: 120, editable: false, valueFormatter: fmtDate },
  { field: 'daysToExpire', headerName: t('optionsBlackScholes.columns.daysToExpire'), width: 110, ...N },
  // resolved inputs
  { field: 'baseAssetShovi', headerName: t('optionsBlackScholes.columns.baseAssetShovi'), width: 120, ...N },
  { field: 'ivBaseAsset', headerName: t('optionsBlackScholes.columns.ivBaseAsset'), width: 120, ...N },
  { field: 'ribitBankIsrael', headerName: t('optionsBlackScholes.columns.ribitBankIsrael'), width: 120, ...N },
  // outputs (current)
  { field: 'bs', headerName: t('optionsBlackScholes.columns.bs'), width: 120, ...N },
  { field: 'delta', headerName: t('optionsBlackScholes.columns.delta'), width: 110, ...N },
  // intermediates (current)
  { field: 'd1', headerName: t('optionsBlackScholes.columns.d1'), width: 110, ...N },
  { field: 'd2', headerName: t('optionsBlackScholes.columns.d2'), width: 110, ...N },
  { field: 'nd1', headerName: t('optionsBlackScholes.columns.nd1'), width: 110, ...N },
  { field: 'nd2', headerName: t('optionsBlackScholes.columns.nd2'), width: 110, ...N },
  { field: 'negNd1', headerName: t('optionsBlackScholes.columns.negNd1'), width: 110, ...N },
  { field: 'negNd2', headerName: t('optionsBlackScholes.columns.negNd2'), width: 110, ...N },
  // opening-rate ("Base") variant
  { field: 'baseAssetShoviBase', headerName: t('optionsBlackScholes.columns.baseAssetShoviBase'), width: 130, ...N },
  { field: 'bsBase', headerName: t('optionsBlackScholes.columns.bsBase'), width: 120, ...N },
  { field: 'deltaBase', headerName: t('optionsBlackScholes.columns.deltaBase'), width: 120, ...N },
  { field: 'd1Base', headerName: t('optionsBlackScholes.columns.d1Base'), width: 110, ...N },
  { field: 'd2Base', headerName: t('optionsBlackScholes.columns.d2Base'), width: 110, ...N },
  { field: 'nd1Base', headerName: t('optionsBlackScholes.columns.nd1Base'), width: 110, ...N },
  { field: 'nd2Base', headerName: t('optionsBlackScholes.columns.nd2Base'), width: 110, ...N },
  { field: 'negNd1Base', headerName: t('optionsBlackScholes.columns.negNd1Base'), width: 110, ...N },
  { field: 'negNd2Base', headerName: t('optionsBlackScholes.columns.negNd2Base'), width: 110, ...N },
];

const newRowDefaults = (): Partial<OptionBlackScholes> => ({});

export function OptionsBlackScholesPage() {
  const getRows = useCallback(() => getOptionsBlackScholes(), []);
  // No-op: read-only page. LopCrudPage requires the prop but never invokes it.
  const saveBatch = useCallback(async () => [], []);

  return (
    <LopCrudPage<OptionBlackScholes>
      resourceCode="optionsBlackScholes"
      titleKey="optionsBlackScholes.pageTitle"
      idField="assetId"
      getRows={getRows}
      saveBatch={saveBatch}
      columnDefs={columnDefs}
      newRowDefaults={newRowDefaults}
      showAdd={false}
      showDelete={false}
    />
  );
}
